import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { MaskLines } from "@/components/Reveal";

const TOPICS = ["High-risk processing", "POS terminals", "Cross-border / FinBPM", "Partnership", "Other"];
const INIT = { name: "", email: "", phone: "", company: "", topic: TOPICS[0], message: "" };

export default function Contact() {
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setSent(true);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <main className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-28 pt-36 sm:px-10 lg:grid-cols-12" data-testid="contact-page">
      <aside className="lg:col-span-5">
        <p className="eyebrow">Contact us</p>
        <h1 className="mt-5 font-display text-5xl font-semibold leading-[0.95] tracking-tight sm:text-6xl">
          <MaskLines lines={["Talk to the", { text: "US desk.", className: "text-[var(--gold)] italic font-light" }]} />
        </h1>
        <p className="mt-6 max-w-md text-slate-300">Questions about high-risk processing, hardware or cross-border settlement with FinBPM. We reply within one business day.</p>
        <p className="mt-10 text-sm text-slate-400">Ready to board? <Link to="/apply" className="text-white underline underline-offset-4 hover:text-[var(--gold)]" data-testid="contact-apply-link">Start an application</Link></p>
      </aside>
      <section className="border border-white/10 bg-[#0d1524] p-6 sm:p-10 lg:col-span-7">
        {sent ? (
          <div data-testid="contact-success">
            <p className="eyebrow">Message sent</p>
            <h2 className="mt-4 font-display text-3xl font-semibold">Thanks, {f.name}. We'll be in touch.</h2>
            <p className="mt-3 text-slate-300">A confirmation is on its way to {f.email}. We'll reply within one business day.</p>
            <button onClick={() => { setF(INIT); setSent(false); }} className="btn-ghost mt-8" data-testid="contact-send-another">Send another message</button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="contact-form">
            <input className="field" required minLength={2} placeholder="Full name *" value={f.name} onChange={set("name")} data-testid="contact-name" />
            <input className="field" required type="email" placeholder="Email *" value={f.email} onChange={set("email")} data-testid="contact-email" />
            <input className="field" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="contact-phone" />
            <input className="field" placeholder="Company" value={f.company} onChange={set("company")} data-testid="contact-company" />
            <select className="field sm:col-span-2" value={f.topic} onChange={set("topic")} data-testid="contact-topic">
              {TOPICS.map((t) => <option key={t}>{t}</option>)}
            </select>
            <textarea className="field sm:col-span-2" required minLength={5} rows={6} placeholder="How can we help? *" value={f.message} onChange={set("message")} data-testid="contact-message" />
            <button disabled={busy} className="btn-primary justify-center sm:col-span-2" data-testid="contact-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
